import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { useLoyaltyOffersHubRemoteConfig } from 'loyalty/hooks/useLoyaltyOffersHubRemoteConfig'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { useAppSelector } from 'hooks/reduxHooks'
import useDelayedJumpToHash from 'hooks/useDelayedJumpToHash'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import LoyaltyOffersHubHeroCarousel from './LoyaltyOffersHubHeroCarousel'
import LoyaltyOffersHubLimitedTimeOffersSection from './LoyaltyOffersHubLimitedTimeOffersSection'
import LoyaltyOffersHubPartnerOffersSection from './LoyaltyOffersHubPartnerOffersSection'

/** Leave compact-header clearance without pushing the anchored heading too far down on phones. */
const HASH_SCROLL_OFFSET = -96

const HeroSection = styled.div`
  padding: ${rem(12)} 0 ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)} 0;
  }

  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const HeroContent = styled(Group)`
  ${tierNormalContrastColorStyle}
`

const BackLink = styled(ClickableLink)`
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`

const OffersBody = styled.div`
  padding-block: ${rem(24)} ${rem(40)};
`

function LoyaltyOffersHubPage() {
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const { currentRegionCode } = useContext(GeoContext)
  const { hasActiveContent, hasResolvedRemoteConfig, heroCarouselItems, industryFilters, limitedTimeOffers, partnerOffers } = useLoyaltyOffersHubRemoteConfig()
  const loyaltyLandingPagePath = `/${currentRegionCode.toLowerCase()}/${LOYALTY_LANDING_PAGE}`
  useDelayedJumpToHash(1000, HASH_SCROLL_OFFSET)

  if (!hasResolvedRemoteConfig) {
    return <LoadingIndicator />
  }

  if (!hasActiveContent) {
    return <RedirectWithStatus code={302} to={loyaltyLandingPagePath} />
  }

  return <>
    <ProductPaletteProvider palette={`tier-${tier}`}>
      <HeroSection className={`tier-${tier}`}>
        <LayoutContainer variant="large">
          <HeroContent className={`tier-${tier}`} direction="vertical" gap={20}>
            <BackLink to={`/${LOYALTY_LANDING_PAGE}`}>
              <BodyTextBlock variant="medium" startIcon={<LineAngleLeftIcon />}>
                {intl.formatMessage({
                  id: 'yMv1Ktx7jFDszD3Q8KUc',
                  defaultMessage: 'Your Membership',
                  description: 'Breadcrumb link text back to the Societe membership page',
                })}
              </BodyTextBlock>
            </BackLink>
            <Heading variant="heading1" as="h1">
              {intl.formatMessage({
                id: '7E0lj3HIEuWLR7A65wue',
                defaultMessage: 'Société Offers',
                description: 'Main page title shown at the top of the Societe offers hub page',
              })}
            </Heading>
            <LoyaltyOffersHubHeroCarousel items={heroCarouselItems} />
          </HeroContent>
        </LayoutContainer>
      </HeroSection>
    </ProductPaletteProvider>
    <LayoutContainer variant="large">
      <OffersBody>
        <Group direction="vertical" gap={0}>
          <LoyaltyOffersHubPartnerOffersSection offers={partnerOffers} industryFilters={industryFilters} />
          <LoyaltyOffersHubLimitedTimeOffersSection offers={limitedTimeOffers} />
        </Group>
      </OffersBody>
    </LayoutContainer>
  </>
}

export default LoyaltyOffersHubPage
